import {
  Sparkles,
  Compass,
  BookOpen,
  Home,
  Target,
  Rocket,
  Search,
  TrendingUp,
  Heart,
  Users,
  type LucideIcon,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserStage =
  | "brand_new"
  | "exploring"
  | "started"
  | "engaged"
  | "active";

export interface CompanionMessage {
  id: string;
  icon: LucideIcon;
  emoji: string;
  titleKey: string;        // i18n translation key
  messageKey: string;      // i18n translation key
  celebration?: boolean;
  dismissible: boolean;
  autoHideSeconds?: number;
  action?: { labelKey: string; label: string; route: string };
  image?: string;          // URL — opens in lightbox
  video?: string;          // URL — opens in lightbox
  // Resolved by the hook (consumer-facing)
  title: string;
  message: string;
}

// Internal config type — stores only keys, resolved by the hook
interface CompanionMessageConfig {
  id: string;
  icon: LucideIcon;
  emoji: string;
  titleKey: string;
  messageKey: string;
  celebration?: boolean;
  dismissible: boolean;
  autoHideSeconds?: number;
  action?: { labelKey: string; route: string };
  image?: string;
  video?: string;
}

// Route keys for the companion guide
type RouteKey =
  | "first_login"
  | "marketplace"
  | "portal_activities"
  | "portal_workspace"
  | "spaces"
  | "examples"
  | "activity_runner"
  | "ai_wizard";

// ─── Companion Messages Map ──────────────────────────────────────────────────

const companionMessages: Record<
  RouteKey,
  Partial<Record<UserStage, CompanionMessageConfig>>
> = {
  first_login: {
    brand_new: {
      id: "first_login_welcome",
      icon: Sparkles,
      emoji: "\u2728",
      titleKey: "companion_first_login_title",
      messageKey: "companion_first_login_message",
      dismissible: true,
      action: { labelKey: "companion_first_login_action", route: "/marketplace" },
    },
  },

  marketplace: {
    brand_new: {
      id: "marketplace_brand_new",
      icon: Compass,
      emoji: "\uD83C\uDF1F",
      titleKey: "companion_marketplace_brand_new_title",
      messageKey: "companion_marketplace_brand_new_message",
      dismissible: true,
      video: "https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Explore%20Marketplace%20Features%20And%20Goal%20Setting%20In%20ClaryNext.mp4"
    },
    exploring: {
      id: "marketplace_exploring",
      icon: Search,
      emoji: "\uD83D\uDD0D",
      titleKey: "companion_marketplace_exploring_title",
      messageKey: "companion_marketplace_exploring_message",
      dismissible: true,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Explore%20Marketplace%20Features%20And%20Goal%20Setting%20In%20ClaryNext.mp4"
    },
    started: {
      id: "marketplace_started",
      icon: Rocket,
      emoji: "\uD83D\uDE80",
      titleKey: "companion_marketplace_started_title",
      messageKey: "companion_marketplace_started_message",
      dismissible: true,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Explore%20Marketplace%20Features%20And%20Goal%20Setting%20In%20ClaryNext.mp4"
    },
  },

  portal_activities: {
    brand_new: {
      id: "portal_activities_brand_new",
      icon: BookOpen,
      emoji: "\uD83D\uDCDA",
      titleKey: "companion_portal_activities_brand_new_title",
      messageKey: "companion_portal_activities_brand_new_message",
      dismissible: true,
      autoHideSeconds: 15,
      action: { labelKey: "companion_portal_activities_brand_new_action", route: "/marketplace" },
    },
    exploring: {
      id: "portal_activities_exploring",
      icon: Compass,
      emoji: "\uD83D\uDC4B",
      titleKey: "companion_portal_activities_exploring_title",
      messageKey: "companion_portal_activities_exploring_message",
      dismissible: true,
      autoHideSeconds: 15,
    },
    started: {
      id: "portal_activities_started",
      icon: TrendingUp,
      emoji: "\uD83D\uDCAA",
      titleKey: "companion_portal_activities_started_title",
      messageKey: "companion_portal_activities_started_message",
      dismissible: true,
      autoHideSeconds: 15,
    },
  },

  portal_workspace: {
    brand_new: {
      id: "portal_workspace_brand_new",
      icon: Home,
      emoji: "\uD83C\uDFE1",
      titleKey: "companion_portal_workspace_brand_new_title",
      messageKey: "companion_portal_workspace_brand_new_message",
      dismissible: true,
      autoHideSeconds: 15,
      video: "https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Capture%20Action%20Items%20And%20Insights%20With%20ClaryNext.mp4"
    },
    exploring: {
      id: "portal_workspace_exploring",
      icon: TrendingUp,
      emoji: "\uD83C\uDF31",
      titleKey: "companion_portal_workspace_exploring_title",
      messageKey: "companion_portal_workspace_exploring_message",
      dismissible: true,
      autoHideSeconds: 15,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Capture%20Action%20Items%20And%20Insights%20With%20ClaryNext.mp4"
    },
    started: {
      id: "portal_workspace_started",
      icon: Target,
      emoji: "\uD83C\uDFAF",
      titleKey: "companion_portal_workspace_started_title",
      messageKey: "companion_portal_workspace_started_message",
      dismissible: true,
      autoHideSeconds: 15,
      video: "https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Capture%20Action%20Items%20And%20Insights%20With%20ClaryNext.mp4"
    },
  },

  spaces: {
    brand_new: {
      id: "spaces_brand_new",
      icon: Compass,
      emoji: "\uD83E\uDDED",
      titleKey: "companion_spaces_brand_new_title",
      messageKey: "companion_spaces_brand_new_message",
      dismissible: true,
    },
    started: {
      id: "spaces_started",
      icon: Sparkles,
      emoji: "\u2728",
      titleKey: "companion_spaces_started_title",
      messageKey: "companion_spaces_started_message",
      dismissible: true,
    },
  },

  examples: {
    brand_new: {
      id: "examples_brand_new",
      icon: Users,
      emoji: "\uD83C\uDF0D",
      titleKey: "companion_examples_brand_new_title",
      messageKey: "companion_examples_brand_new_message",
      dismissible: true,
    },
  },

  activity_runner: {
    brand_new: {
      id: "activity_runner_encouragement",
      icon: Heart,
      emoji: "\uD83D\uDC9C",
      titleKey: "",
      messageKey: "companion_activity_runner_message",
      dismissible: true,
      autoHideSeconds: 6,
    },
    exploring: {
      id: "activity_runner_encouragement",
      icon: Heart,
      emoji: "\uD83D\uDC9C",
      titleKey: "",
      messageKey: "companion_activity_runner_message",
      dismissible: true,
      autoHideSeconds: 6,
    },
    started: {
      id: "activity_runner_encouragement",
      icon: Heart,
      emoji: "\uD83D\uDC9C",
      titleKey: "",
      messageKey: "companion_activity_runner_message",
      dismissible: true,
      autoHideSeconds: 6,
    },
    engaged: {
      id: "activity_runner_encouragement",
      icon: Heart,
      emoji: "\uD83D\uDC9C",
      titleKey: "",
      messageKey: "companion_activity_runner_message",
      dismissible: true,
      autoHideSeconds: 6,
    },
    active: {
      id: "activity_runner_encouragement",
      icon: Heart,
      emoji: "\uD83D\uDC9C",
      titleKey: "",
      messageKey: "companion_activity_runner_message",
      dismissible: true,
      autoHideSeconds: 6,
    },
  },

  ai_wizard: {
    brand_new: {
      id: "ai_wizard_intro",
      icon: Sparkles,
      emoji: "\u2728",
      titleKey: "companion_ai_wizard_title",
      messageKey: "companion_ai_wizard_message",
      dismissible: true,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Generate%20Engaging%20Activities%20With%20AI%20Wizard%20In%20ClaryNext.mp4"
    },
    exploring: {
      id: "ai_wizard_intro",
      icon: Sparkles,
      emoji: "\u2728",
      titleKey: "companion_ai_wizard_title",
      messageKey: "companion_ai_wizard_message",
      dismissible: true,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Generate%20Engaging%20Activities%20With%20AI%20Wizard%20In%20ClaryNext.mp4"
    },
    started: {
      id: "ai_wizard_intro",
      icon: Sparkles,
      emoji: "\u2728",
      titleKey: "companion_ai_wizard_title",
      messageKey: "companion_ai_wizard_message",
      dismissible: true,
      video:"https://pub-3d740db9ce264c46b3b663ac302643dd.r2.dev/videos/Generate%20Engaging%20Activities%20With%20AI%20Wizard%20In%20ClaryNext.mp4"
    },
  },
};

// ─── Discovery Messages ──────────────────────────────────────────────────────

export interface DiscoveryCelebration {
  id: string;
  emoji: string;
  messageKey: string;  // i18n translation key
  // Resolved by the hook
  message: string;
}

// Internal config — stores only keys
interface DiscoveryCelebrationConfig {
  id: string;
  emoji: string;
  messageKey: string;
}

export const discoveryCelebrations: Record<string, DiscoveryCelebrationConfig> = {
  // Route-based discoveries
  marketplace: {
    id: "discovery_marketplace",
    emoji: "\uD83C\uDF1F",
    messageKey: "companion_discovery_marketplace",
  },
  portal_workspace: {
    id: "discovery_workspace",
    emoji: "\uD83C\uDFE1",
    messageKey: "companion_discovery_workspace",
  },
  portal_activities: {
    id: "discovery_activities",
    emoji: "\uD83D\uDCDA",
    messageKey: "companion_discovery_activities",
  },
  spaces: {
    id: "discovery_spaces",
    emoji: "\uD83E\uDDED",
    messageKey: "companion_discovery_spaces",
  },
  examples: {
    id: "discovery_examples",
    emoji: "\uD83C\uDF0D",
    messageKey: "companion_discovery_examples",
  },
  // Event-based celebrations
  first_engagement: {
    id: "discovery_first_engagement",
    emoji: "\uD83C\uDFAF",
    messageKey: "companion_discovery_first_engagement",
  },
  first_activity_created: {
    id: "discovery_first_activity_created",
    emoji: "\u2728",
    messageKey: "companion_discovery_first_activity_created",
  },
  first_purchase: {
    id: "discovery_first_purchase",
    emoji: "\uD83C\uDF89",
    messageKey: "companion_discovery_first_purchase",
  },
  first_takeaway: {
    id: "discovery_first_takeaway",
    emoji: "\uD83D\uDCDD",
    messageKey: "companion_discovery_first_takeaway",
  },
  first_insight: {
    id: "discovery_first_insight",
    emoji: "\uD83D\uDCA1",
    messageKey: "companion_discovery_first_insight",
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getCompanionMessageConfig(
  routeKey: RouteKey,
  stage: UserStage
): CompanionMessageConfig | null {
  const routeMessages = companionMessages[routeKey];
  if (!routeMessages) return null;

  // Try exact stage, then fall through to earlier stages
  const fallbackOrder: UserStage[] = [
    "brand_new",
    "exploring",
    "started",
    "engaged",
    "active",
  ];
  const stageIndex = fallbackOrder.indexOf(stage);

  // First try exact match
  if (routeMessages[stage]) return routeMessages[stage]!;

  // For "engaged" and "active" users, don't show FE guidance (BE takes over)
  if (stageIndex >= 3) return null;

  // Fall back to closest earlier stage
  for (let i = stageIndex - 1; i >= 0; i--) {
    const fallbackStage = fallbackOrder[i];
    if (routeMessages[fallbackStage]) return routeMessages[fallbackStage]!;
  }

  return null;
}

export function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export function getTimeGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "greeting_morning";
  if (hour < 17) return "greeting_afternoon";
  return "greeting_evening";
}

export function getTimeGreetingEmoji(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "\u2600\uFE0F"; // ☀️
  if (hour < 17) return "\uD83D\uDC4B"; // 👋
  return "\uD83C\uDF19";                // 🌙
}
